import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { BarChart3, Clock3, Trophy } from "lucide-react";
import { useState } from "react";

import { api } from "@/api";
import {
  EmptyState,
  ErrorState,
  money,
  Panel,
  Skeleton,
} from "@/components/ui";
import { useRealtimeTopic } from "@/realtime/RealtimeProvider";

export default function WageringLeaderboardPage() {
  const [period, setPeriod] = useState<"daily" | "weekly" | "monthly">(
    "weekly",
  );
  const [onlyMe, setOnlyMe] = useState(false);
  useRealtimeTopic("world.races");

  const board = useQuery({
    queryKey: ["leaderboards", "wagering", period],
    queryFn: () => api.wageringLeaderboard(period),
    refetchInterval: 15_000,
  });
  const rows = onlyMe
    ? (board.data?.rows.filter((row) => row.isViewer) ?? [])
    : (board.data?.rows ?? []);

  return (
    <>
      <Panel className="leaderboard-hero wagering-hero">
        <div>
          <span>Cosmetic competition</span>
          <h1>Wagering Leaderboard</h1>
          <p>
            Risk-weighted wagering points from settled platform coupons. This
            leaderboard moves no money and has no prize budget.
          </p>
          <small>
            <Trophy aria-hidden /> Current season
          </small>
        </div>
        <div className="leaderboard-tabs">
          {(["daily", "weekly", "monthly"] as const).map((value) => (
            <button
              className={clsx(period === value && "is-active")}
              key={value}
              onClick={() => setPeriod(value)}
              type="button"
            >
              {value}
            </button>
          ))}
        </div>
      </Panel>

      <div className="wagering-meta">
        <div>
          <Clock3 aria-hidden />
          <span>
            Ends{" "}
            {board.data
              ? new Date(board.data.endsAt).toLocaleString()
              : "loading…"}
          </span>
        </div>
        <div className="scope-tabs">
          <button
            className={clsx(!onlyMe && "is-active")}
            onClick={() => setOnlyMe(false)}
            type="button"
          >
            Everyone
          </button>
          <button
            className={clsx(onlyMe && "is-active")}
            onClick={() => setOnlyMe(true)}
            type="button"
          >
            Only me
          </button>
        </div>
      </div>

      {board.isPending ? (
        <Skeleton rows={10} variant="leaderboard" />
      ) : board.error ? (
        <ErrorState error={board.error} retry={() => void board.refetch()} />
      ) : rows.length ? (
        <Panel className="wagering-board">
          <header>
            <span>Rank</span>
            <span>Player</span>
            <span>Bets</span>
            <span>Wagered</span>
            <span>Returned</span>
            <span>Points</span>
          </header>
          {rows.map((row) => (
            <article
              className={clsx(
                "wagering-row",
                row.isViewer && "is-viewer",
                row.rank <= 3 && `is-podium is-podium--${row.rank}`,
              )}
              key={row.playerId}
            >
              <b className={`rank rank--${row.rank}`}>{row.rank}</b>
              <span>{row.displayName}</span>
              <span>{row.bets}</span>
              <span>{money(row.wageredCents)}</span>
              <span>{money(row.returnedCents)}</span>
              <strong>{row.points.toLocaleString()}</strong>
            </article>
          ))}
        </Panel>
      ) : (
        <EmptyState
          title={
            onlyMe
              ? "You are not ranked in this period"
              : "No wagers in this period"
          }
        />
      )}

      <Panel className="scoring-note">
        <BarChart3 aria-hidden />
        <span>
          Points reward stake and difficulty, with a landed-bet bonus. They are
          display-only and never affect balance, payout, odds or limits.
        </span>
      </Panel>
    </>
  );
}
